/* ============================================================================
 * Login — the session gate
 * ----------------------------------------------------------------------------
 * Boot powers the machine on; this decides who it works for. Two columns, two
 * questions: WHO (the lockup and the choice) and WHAT (what signing in does).
 *
 * SELF-CUSTODY, STATED ON ITS FACE. Nothing here signs, approves or moves
 * anything — connecting a wallet grants READS: balance, ENS, attestations. The
 * screen says that where a user is most likely to assume the opposite, which is
 * the moment just before they click.
 *
 * GUEST IS NOT A LESSER STATE. The OS is fully usable read-only, so the guest
 * path is present on every visit. When the browser has no injected wallet at
 * all, guest takes the PRIMARY slot and the wallet option becomes a blocked row
 * naming the reason — a dead button teaches nothing.
 *
 * NO AUTO-POPUP (src/main.tsx, useWallet): connect() runs only from the click
 * below. Nothing is requested from a wallet before then.
 * ========================================================================= */

import { useEffect, useRef, useState } from 'react';
import { useAppearance } from './AppearanceProvider';
import { skinDef } from '../hooks/useSkin';
import { useWallet } from '../hooks/useWallet';
import { CHAINS, DEFAULT_CHAIN_ID, base, baseSepolia, mainnet } from '../lib/supercompute';

const VERSION = 'sovereign webos 0.1.0';

/**
 * The chains this machine touches, and what each one is for. Volume 03 of the
 * system requires this on the session surface: the session runs on one chain
 * while reads land on others, and hiding that is what makes an operator
 * distrust an address they actually read correctly. Jobs and chains are the
 * repo's own documented split — not a summary invented for this screen.
 */
const NETWORKS: { job: string; chain: { name: string; id: number }; note: string }[] = [
  { job: 'presence', chain: baseSepolia, note: 'attestations' },
  { job: 'capital', chain: base, note: 'aragon dao' },
  { job: 'identity', chain: mainnet, note: 'ENS · text records' },
];

/** Resolve a chain label from the constant itself — never a typed string. */
function chainLabel(id: number): string {
  const c = CHAINS.find((c) => c.id === id);
  return c ? `${c.name} · ${c.id}` : `chain ${id}`;
}

export function Login({ onDone }: { onDone: () => void }) {
  const { skin } = useAppearance();
  const brand = skinDef(skin);
  const w = useWallet();

  const [asked, setAsked] = useState(false);
  const primary = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primary.current?.focus();
  }, []);

  // Clear the awaiting latch the moment the wallet answers either way.
  useEffect(() => {
    if (w.isConnected || w.error) setAsked(false);
  }, [w.isConnected, w.error]);

  /* Is there a wallet to connect to? `connectors[0]` is the `injected()`
   * connector declared in lib/wagmi.ts and exists whether or not a provider
   * does — so the connector list is NOT the test. A live injected provider, or
   * more than one discovered connector (EIP-6963 registration), is. */
  const injected =
    typeof window !== 'undefined' &&
    Boolean((window as unknown as { ethereum?: unknown }).ethereum);
  const hasWallet = injected || w.availableConnectors.length > 1;

  const connected = w.isConnected;
  const awaiting = w.isConnecting || asked;
  const transport = w.availableConnectors[0]?.name;

  const label = connected
    ? 'ENTER THE DESKTOP'
    : hasWallet
      ? awaiting
        ? 'AWAITING WALLET…'
        : 'CONNECT WALLET'
      : 'CONTINUE AS GUEST';

  const onPrimary = () => {
    if (connected || !hasWallet) {
      onDone();
      return;
    }
    setAsked(true);
    try {
      w.connect();
    } catch {
      setAsked(false);
    }
  };

  return (
    <div className="login wos-crt">
      <div
        className="login__plate dsp"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-ask"
      >
        {/* ── left: the machine asks ─────────────────────────────────────── */}
        <div className="login__id">
          <div className="login__lockup">
            <img
              className="brandmark pix"
              src={brand.mark}
              width={64}
              height={64}
              alt=""
            />
            <div className="login__lockup-t">
              <h1 className="login__word">{brand.wordmark}</h1>
              <p className="login__handle">{brand.handle.toUpperCase()}</p>
            </div>
          </div>
          {brand.tagline && <p className="login__tag">{brand.tagline}</p>}

          <div className="login__rule" aria-hidden="true" />

          <p className="eyebrow login__eyebrow">session gate</p>
          <h2 className="login__ask" id="login-ask">
            Who is this machine working for?
          </h2>
          <p className="cap login__note">
            Signing in lets this OS <b>read</b> — your balance, your ENS name,
            your attestations. It does not sign, approve or move anything, and
            the keys never leave your browser.
          </p>

          <div className="login__acts">
            <button
              ref={primary}
              type="button"
              className="btn btn--primary login__act"
              onClick={onPrimary}
              disabled={hasWallet && !connected && awaiting}
            >
              {label}
            </button>

            {hasWallet && !connected && (
              <button
                type="button"
                className="btn login__act login__act--ghost"
                onClick={onDone}
              >
                Continue as guest
              </button>
            )}
          </div>

          {!hasWallet && (
            <p className="login__blocked">
              <b>wallet not detected</b>
              No injected wallet answered in this browser, so there is nothing to
              connect to. Everything in the OS is readable as a guest. To sign in
              with an address, open this desk in a browser that has one.
            </p>
          )}

          {hasWallet && !connected && (
            <p className="login__blocked">
              <b>what connecting does</b>
              Your wallet will ask you to approve the connection. Nothing is
              signed and nothing moves — this OS only reads. You can disconnect
              at any time.
            </p>
          )}

          {connected && (
            <p className="login__blocked">
              <b>signed in · read only</b>
              This session can read your balance, your ENS name and your
              attestations. It cannot sign, approve or move anything, and
              disconnecting returns the desk to the guest view.
            </p>
          )}

          {w.error && (
            <p className="err login__err" role="alert">
              {w.error}
            </p>
          )}
        </div>

        {/* ── right: the machine states ─────────────────────────────────── */}
        <aside className="login__sess" aria-label="Session">
          <h3 className="login__sess-h">Session</h3>

          {connected && (
            <div className="login__who">
              <img
                className="login__avatar pix"
                src={w.ensAvatar ?? brand.mark}
                width={40}
                height={40}
                alt=""
              />
              <span className="login__who-h">{w.handle}</span>
              <span className="login__addr">{w.address}</span>
            </div>
          )}

          <dl className="login__kv">
            <div>
              <dt>operator</dt>
              <dd>{connected ? w.handle : 'guest'}</dd>
            </div>
            <div>
              <dt>ladder</dt>
              <dd>ENS → short address → guest</dd>
            </div>
            <div>
              <dt>session chain</dt>
              <dd>{chainLabel(DEFAULT_CHAIN_ID)}</dd>
            </div>
            <div>
              <dt>transport</dt>
              <dd>
                {connected
                  ? (transport ?? 'injected wallet')
                  : hasWallet
                    ? (transport ?? 'injected wallet')
                    : 'none detected'}
              </dd>
            </div>
            <div>
              <dt>connect on load</dt>
              <dd>never</dd>
            </div>
            <div>
              <dt>keys</dt>
              <dd>in your browser, not here</dd>
            </div>
            <div>
              <dt>signs</dt>
              <dd>nothing</dd>
            </div>
            <div>
              <dt>moves</dt>
              <dd>nothing</dd>
            </div>
          </dl>

          <h3 className="login__sess-h login__sess-h--net">Networks</h3>
          <dl className="login__kv">
            {NETWORKS.map((n) => (
              <div key={n.job}>
                <dt>{n.job}</dt>
                <dd>
                  {n.chain.name} · {n.chain.id}
                  <span className="login__note-n">{n.note}</span>
                </dd>
              </div>
            ))}
          </dl>

          <p className="login__src">
            read from the single hook that owns account state · no chain call is
            made before a click
          </p>
        </aside>
        {/* ── the provenance line that closes the page ──────────────────── */}
        <div className="login__foot">
          <span>{VERSION}</span>
          <span className="login__foot-mid">
            nothing signed · keys never leave the browser
          </span>
          <span>{connected ? w.handle : 'guest'}</span>
        </div>
      </div>
    </div>
  );
}
